import React, { useRef, useState } from "react";
import { useParams } from 'react-router-dom';
import ConfigFile from "./ParsePanelData";
import ConfigFileGenerator from "./GenerateConfigFile";
import Axios from "axios";
import * as XLSX from "xlsx";
import { toast, ToastContainer } from 'react-toastify';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button } from "@mui/material";

const MainContent = ({ project, updatePanels, panelVersion }) => {
    
    const [configFileData, setConfigFileData] = useState(null);
    const { mdId } = useParams();
    const siId = localStorage.getItem("siId");

    // ===== Upload .cfg: ask for Project Name first =====
    const [isCfgUploadDialogOpen, setIsCfgUploadDialogOpen] = useState(false);
    const [cfgProjectName, setCfgProjectName] = useState("");
    const cfgUploadInputRef = useRef(null);

    const openCfgUploadDialog = () => setIsCfgUploadDialogOpen(true);
    const closeCfgUploadDialog = () => setIsCfgUploadDialogOpen(false);

    const confirmCfgUploadDialog = () => {
        if (!cfgProjectName.trim()) {
            toast.warning("Please enter a project name.");
            return;
        }
        closeCfgUploadDialog();
        cfgUploadInputRef.current?.click();
    };

    const handleFileUpload = (event) => {
        const file = event.target.files?.[0];
        // allow selecting the same file again
        event.target.value = "";
        if (!file) return;

        const projectName = (cfgProjectName || "").trim() || "Unnamed Project";

        const reader = new FileReader();
        reader.onload = (e) => {
            const configFile = new ConfigFile(panelVersion);
            configFile.readFile(e.target.result);
            setConfigFileData(configFile.VT5000_CONFIG); // Store the parsed configuration data
            console.log("Parsed Config:", configFile.VT5000_CONFIG);

            const userEmail = localStorage.getItem('userEmail');
            Axios.post(`${process.env.REACT_APP_BACKEND_URL}/add_customer_project`, {
                mdId: mdId,
                siId: siId,
                email: userEmail,
                projectName: projectName,
                panel: configFile.VT5000_CONFIG,
            })
                .then((response) => {
                    if (response.data.success) {
                        toast.success("Panel added successfully!");
                        updatePanels(response.data.project_id); // pass project_id
                    } else {
                        toast.error("Panel already exists!");
                    }
                })
                .catch((error) => {
                    console.error("Search panel failed!", error);
                    toast.error("Failed to check panel!");
                });
        };
        reader.readAsArrayBuffer(file); // Read the file as ArrayBuffer
    };

    const handleExcelUpload = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            const data = new Uint8Array(e.target.result);

            const workbook = XLSX.read(data, {
                type: "array",
                codepage: 65001 // Optional: Use correct encoding
            });

            const zonesSet = new Set();
            for (let i = 0; i < workbook.SheetNames.length; i++) {
                const sheetName = workbook.SheetNames[i]; // You can loop through if needed
                const worksheet = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
                for (let j = 2; j < jsonData.length; j++) {
                    const row = jsonData[j];
                    const deviceType = (typeof row[1] === "string" ? row[1].toLowerCase() : "").trim(); // Device Type
                    const type = deviceType.toLowerCase();
                    const deviceName = row[2] || "";
                    // No change for Identifier.
                    const zone = row[4] || 0;
                    const zone2 = row[5] || 0;
                    const zone3 = row[6] || 0;
                    const zone4 = row[7] || 0;
                    [zone, zone2, zone3, zone4].forEach(z => {
                        if (z && z > 0) {
                            zonesSet.add(z);
                        }
                    });
                    const device = project.panel.VT5000_CONFIG.ArgusTablePr?.[i]?.[j - 2];
                    if (device) {
                        device.lastTipo = 0;
                        device.isMultiSensor = 0;
                        device.deviceType = 0;
                        device.isCallPoint = 0;
                        device.isGas = 0;
                        device.isFlasher = 0;
                        device.isSounder = 0;
                        device.isRepeater = 0;
                        if (type === "smoke") {
                            device.lastTipo = 0;
                            device.isMultiSensor = 0;
                            device.deviceType = 0;
                            device.isCallPoint = 0;
                            device.isGas = 0;
                            device.isFlasher = 0;
                            device.isSounder = 0;
                            device.isRepeater = 0;
                            device.programmed = 1;
                        }
                        else if (type === "thermal") {
                            device.lastTipo = 1;
                            device.isMultiSensor = 0;
                            device.deviceType = 0;
                            device.isCallPoint = 0;
                            device.isGas = 0;
                            device.isFlasher = 0;
                            device.isSounder = 0;
                            device.isRepeater = 0;
                            device.programmed = 1;
                        }
                        else if (type === "smoke therm.") {
                            device.lastTipo = 0;
                            device.isMultiSensor = 1;
                            device.deviceType = 0;
                            device.isCallPoint = 0;
                            device.isGas = 0;
                            device.isFlasher = 0;
                            device.isSounder = 0;
                            device.isRepeater = 0;
                            device.programmed = 1;
                        }
                        else if (type === "call point") {
                            device.lastTipo = 3;
                            device.isMultiSensor = 0;
                            device.deviceType = 1;
                            device.isCallPoint = 1;
                            device.isGas = 0;
                            device.isFlasher = 0;
                            device.isSounder = 0;
                            device.isRepeater = 0;
                            device.programmed = 1;
                        }
                        else if (type === "gas") {
                            device.lastTipo = 3;
                            device.isMultiSensor = 0;
                            device.deviceType = 1;
                            device.isCallPoint = 0;
                            device.isGas = 1;
                            device.isFlasher = 0;
                            device.isSounder = 0;
                            device.isRepeater = 0;
                            device.programmed = 1;
                        }
                        else if (type === "input") {
                            device.lastTipo = 3;
                            device.isMultiSensor = 0;
                            device.deviceType = 1;
                            device.isCallPoint = 0;
                            device.isGas = 0;
                            device.isFlasher = 0;
                            device.isSounder = 0;
                            device.isRepeater = 0;
                            device.programmed = 1;
                        }
                        else if (type === "flasher") {
                            device.lastTipo = 4;
                            device.isMultiSensor = 0;
                            device.deviceType = 1;
                            device.isCallPoint = 0;
                            device.isGas = 0;
                            device.isFlasher = 1;
                            device.isSounder = 0;
                            device.isRepeater = 0;
                            device.programmed = 1;
                        }
                        else if (type === "siren") {
                            device.lastTipo = 4;
                            device.isMultiSensor = 0;
                            device.deviceType = 1;
                            device.isCallPoint = 0;
                            device.isGas = 0;
                            device.isFlasher = 0;
                            device.isSounder = 1;
                            device.isRepeater = 0;
                            device.programmed = 1;
                        }
                        else if (type === "rep.led") {
                            device.lastTipo = 4;
                            device.isMultiSensor = 0;
                            device.deviceType = 1;
                            device.isCallPoint = 0;
                            device.isGas = 0;
                            device.isFlasher = 0;
                            device.isSounder = 0;
                            device.isRepeater = 1;
                            device.programmed = 1;
                        }
                        else if (type === "output") {
                            device.lastTipo = 4;
                            device.isMultiSensor = 0;
                            device.deviceType = 1;
                            device.isCallPoint = 0;
                            device.isGas = 0;
                            device.isFlasher = 0;
                            device.isSounder = 0;
                            device.isRepeater = 0;
                            device.programmed = 1;
                        }
                        else if (type === "xpander") {
                            device.lastTipo = 5;
                            device.isMultiSensor = 0;
                            device.deviceType = 1;
                            device.isCallPoint = 0;
                            device.isGas = 0;
                            device.isFlasher = 0;
                            device.isSounder = 0;
                            device.isRepeater = 0;
                            device.programmed = 1;
                        }
                        else {
                            device.lastTipo = 0;
                            device.isMultiSensor = 0;
                            device.deviceType = 0;
                            device.isCallPoint = 0;
                            device.isGas = 0;
                            device.isFlasher = 0;
                            device.isSounder = 0;
                            device.isRepeater = 0;
                            device.programmed = 0;
                        }
                        device.name = deviceName;
                        device.zone = zone;
                        device.zone2 = zone2;
                        device.zone3 = zone3;
                        device.zone4 = zone4;
                    }
                }
            }

            zonesSet.forEach((z) => {
                if (project &&
                    project.panel &&
                    project.panel.VT5000_CONFIG &&
                    project.panel.VT5000_CONFIG.ZoneTable &&
                    project.panel.VT5000_CONFIG.ZoneTable[z - 1]) {
                    project.panel.VT5000_CONFIG.ZoneTable[z - 1].programmed = 1;
                }
            });

            const userEmail = localStorage.getItem('userEmail');
            Axios.post(`${process.env.REACT_APP_BACKEND_URL}/update_panel_devices`, {
                projectId: project.project_id,
                argusTablePr: project.panel.VT5000_CONFIG.ArgusTablePr
            })
                .then((response) => {
                    if (response.data.success) {
                        toast.success('Successfully updated the Excel Template', {
                            position: "top-right",
                            autoClose: 5000,
                            hideProgressBar: false,
                            closeOnClick: true,
                            pauseOnHover: true,
                            draggable: true,
                            progress: undefined,
                            theme: "light",
                        });
                    } else {
                        toast.error(response.data.message || "Failed to update Excel template", {
                            position: "top-right",
                            autoClose: 5000,
                            hideProgressBar: false,
                            closeOnClick: true,
                            pauseOnHover: true,
                            draggable: true,
                            progress: undefined,
                            theme: "light",
                        });
                    }
                })
                .catch((error) => {
                    toast.error("Error updating Excel template" || error, {
                        position: "top-right",
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                        theme: "light",
                    });
                });
        };

        reader.readAsArrayBuffer(file); // Must use ArrayBuffer for binary .xls
    };

    const handleExcelExport = () => {
      
        const loopCardNb = project.panel.VT5000_CONFIG.GeneralParameters?.loopCardNb || 0;
        const numLoops = 1 + 2 * loopCardNb;
        const numDevices = 240;

        const workbook = XLSX.utils.book_new();

        for (let loop = 1; loop <= numLoops; loop++) {
            const sheetData = [];

            // Row 1: Instruction message
            sheetData.push([
                "Please edit or copy paste cells only from cell B3 to B242 and from cell C3 to F242. Do not change any other cells"
            ]);

            // Row 2: Headers
            const headerRow = [];
            headerRow[0] = "Device #";
            headerRow[1] = "Device Type";
            headerRow[2] = "Description (Max 20 Characters. Any additional characters will be deleted)";
            headerRow[3] = "Node #";
            headerRow[4] = "Zone"; // merged E2:H2
            sheetData.push(headerRow);

            // Rows 3 to 242: Device rows
            for (let i = 1; i <= numDevices; i++) {
                const device = project.panel.VT5000_CONFIG.ArgusTablePr?.[loop - 1]?.[i - 1];
                let deviceType = "";
                if (device?.programmed === 1) {
                    const isModule = device.deviceType === 1;
                    const isDevice = device.deviceType === 0;
                    const isSmoke = isDevice && device.lastTipo === 0 && device.isMultiSensor === 0;
                    const isThermal = isDevice && device.lastTipo === 1;
                    const isSmokeTherm = isDevice && device.lastTipo === 0 && device.isMultiSensor === 1;
                    const isCallPoint = isModule && device.lastTipo === 3 && device.isCallPoint === 1;
                    const isGas = isModule && device.lastTipo === 3 && device.isGas === 1;
                    const isInput = isModule && device.lastTipo === 3 && !isCallPoint && !isGas;
                    const isFlasher = isModule && device.lastTipo === 4 && device.isFlasher === 1;
                    const isSiren = isModule && device.lastTipo === 4 && device.isSounder === 1;
                    const isRepeater = isModule && device.lastTipo === 4 && device.isRepeater === 1;
                    const isOutput = isModule && device.lastTipo === 4 && !isFlasher && !isSiren && !isRepeater;
                    const isExpander = isModule && device.lastTipo === 5;

                    if (isSmoke) deviceType = "Smoke";
                    else if (isThermal) deviceType = "Thermal";
                    else if (isSmokeTherm) deviceType = "Smoke Therm.";
                    else if (isCallPoint) deviceType = "Call point";
                    else if (isGas) deviceType = "Gas";
                    else if (isInput) deviceType = "Input";
                    else if (isFlasher) deviceType = "Flasher";
                    else if (isSiren) deviceType = "Siren";
                    else if (isRepeater) deviceType = "Rep.Led";
                    else if (isOutput) deviceType = "Output";
                    else if (isExpander) deviceType = "XPander";
                }
      
                const row = [];
                row[0] = `Loop ${loop} Device ${i}`;
                row[1] = deviceType; // Device type
                if (device.name.length > 0) {
                    row[2] = device.name;
                }
                else {
                    row[2] = "";
                }
                if (project?.panel?.VT5000_CONFIG?.GeneralParameters?.vtnetPanelAddress && project.panel.VT5000_CONFIG.GeneralParameters.vtnetPanelAddress > 0) {
                    row[3] = project.panel.VT5000_CONFIG.GeneralParameters.vtnetPanelAddress;
                }
                else {
                    row[3] = ""; // Node #
                }
                if (device.zone && device.zone > 0) {
                    row[4] = device.zone; // Zone value
                }
                else {
                    row[4] = "";
                }
                if (panelVersion === "2.0.F7") {
                    if (device.zone2 && device.zone2 > 0) {
                        row[5] = device.zone2; // Zone 2 value 
                    }
                    else {
                        row[5] = "";
                    }
                    if (device.zone3 && device.zone3 > 0) {
                        row[6] = device.zone3; // Zone 2 value 
                    }
                    else {
                        row[6] = "";
                    }
                    if (device.zone4 && device.zone4 > 0) {
                        row[7] = device.zone4; // Zone 2 value 
                    }
                    else {
                        row[7] = "";
                    }
                }
                sheetData.push(row);
            }

            // Convert to worksheet
            const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

            // Set up merges
            worksheet["!ref"] = "A1:N243"; // Covers full range
            // Always merge A1:N1
            const merges = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 13 } }];

            // Only merge E2:H2 ("Zone") header in 2.0.F7
            if (panelVersion === "2.0.F7") {
                merges.push({ s: { r: 1, c: 4 }, e: { r: 1, c: 7 } });
            }

            worksheet["!merges"] = merges;
            worksheet["!protect"] = true; // Optional: protect the sheet
            worksheet["!rows"] = Array.from({ length: 243 }, (_, i) =>
                i === 0 ? { hpt: 33 } : {} // Optional: row 1 height
            );

            // Append to workbook
            XLSX.utils.book_append_sheet(workbook, worksheet, `Loop${loop}`);
        }

        const wbout = XLSX.write(workbook, { bookType: "xls", type: "array" });
        const blob = new Blob([wbout], { type: "application/vnd.ms-excel" });

        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        const panelName = project?.panel?.VT5000_CONFIG?.GeneralParameters?.panelName.replace(/\u0000/g, '') || "Unnamed";
        link.download = `Template-${panelName}.xls`;
        link.click();
    };

    const handleExport = () => {

        if (!project) {
            alert("Unknown issue, please refresh the page first!");
            return;
        }

        // Generate the configuration file
        const generator = new ConfigFileGenerator(project.panel.VT5000_CONFIG, panelVersion);
        const generatedBuffer = generator.generateConfigFile(); // Get the buffer for the file

        // Create a blob for the generated file and download it
        const blob = new Blob([generatedBuffer], { type: "application/octet-stream" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "VELOXCONFIG.CFG";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div style={{ flex: "1", padding: "16px", color: "black" }}>
            <div style={{ textAlign: "center", fontSize: "24px", marginBottom: "20px" }}>
                Main Content
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "20px" }}>

                {/* Left side - .cfg actions */}
                <div style={{ flex: "1", minWidth: "250px" }}>
                    <div style={{ fontWeight: "bold", marginBottom: "10px" }}>CFG File</div>

                    <input
                        type="file"
                        accept=".cfg"
                        id="cfg-upload"
                        ref={cfgUploadInputRef}
                        style={{ display: "none" }}
                        onChange={handleFileUpload}
                    />
                    <button
                        className="fancy-button"
                        onClick={openCfgUploadDialog}
                    >
                        Upload .cfg File
                    </button>

                    <button className="fancy-button" onClick={handleExport} style={{ marginLeft: "10px" }}>
                        Export .cfg File
                    </button>

                    <Dialog
                        open={isCfgUploadDialogOpen}
                        onClose={closeCfgUploadDialog}
                        fullWidth
                        maxWidth="sm"
                    >
                        <DialogTitle>Upload .cfg File</DialogTitle>
                        <DialogContent>
                            <TextField
                                label="Project Name"
                                fullWidth
                                autoFocus
                                value={cfgProjectName}
                                onChange={(e) => setCfgProjectName(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") confirmCfgUploadDialog();
                                }}
                                style={{ marginTop: "10px" }}
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={closeCfgUploadDialog}>Cancel</Button>
                            <Button variant="contained" onClick={confirmCfgUploadDialog}>
                                Next
                            </Button>
                        </DialogActions>
                    </Dialog>
                </div>

                {/* Right side - .xls actions */}
                <div style={{ flex: "1", minWidth: "250px" }}>
                    <div style={{ fontWeight: "bold", marginBottom: "10px" }}>Excel Template</div>

                    <input
                        id="xls-upload"
                        type="file"
                        accept=".xls"
                        style={{ display: "none" }}
                        onChange={handleExcelUpload}
                    />
                    <button
                        className="fancy-button"
                        onClick={() => document.getElementById("xls-upload").click()}
                    >
                        Upload Excel (.xls)
                    </button>

                    <button className="fancy-button" onClick={handleExcelExport} style={{ marginLeft: "10px" }}>
                        Export Excel Template
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MainContent;
